import { useEffect, useState } from 'react';
import StarRating from '../common/StarRating';
import { getProductReviews } from '../../api/real/reviewsApi';
import { sourceText } from './reviewSource';

/** "What customers say" chips: counts of the tags customers picked; with the written reviews shown, a chip filters them. */
const TagSummary = ({ tags, active, onPick, clickable }) => {
  const liked = tags.filter((t) => t.kind === 'POSITIVE');
  const better = tags.filter((t) => t.kind !== 'POSITIVE');
  const chip = (t, tone) => {
    const on = active === t.id;
    const cls = `inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${on
      ? 'bg-primary text-white border-primary'
      : tone === 'good' ? 'bg-white text-ink border-border' : 'bg-white text-ink-soft border-border'}`;
    const count = <span className={on ? 'text-white/80' : 'text-ink-muted'}>{t.count}</span>;
    return clickable ? (
      <button key={t.id} type="button" aria-pressed={on} onClick={() => onPick(on ? null : t.id)}
        className={`${cls} ${on ? '' : 'hover:border-primary/50'}`}>{t.label}{count}</button>
    ) : <span key={t.id} className={cls}>{t.label}{count}</span>;
  };
  return (
    <div className="mb-6 space-y-3" data-testid="tag-summary">
      {liked.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-ink mb-2">What customers say</p>
          <div className="flex flex-wrap gap-2">{liked.map((t) => chip(t, 'good'))}</div>
        </div>
      )}
      {better.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-ink-soft mb-2">Some mentioned</p>
          <div className="flex flex-wrap gap-2">{better.map((t) => chip(t, 'better'))}</div>
        </div>
      )}
    </div>
  );
};

/** The product page's Reviews tab: real numbers from approved reviews, and their text when the store shows it. */
const ProductReviewsPanel = ({ productId }) => {
  const [data, setData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [tag, setTag] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setTag(null);
    getProductReviews(productId, { page: 0 })
      .then((d) => { if (alive) { setData(d); setReviews(d?.reviews || []); setPage(0); setTotalPages(d?.totalPages || 0); } })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [productId]);

  const pickTag = async (id) => {
    setTag(id);
    try {
      const d = await getProductReviews(productId, { page: 0, tag: id || undefined });
      setReviews(d?.reviews || []);
      setPage(0);
      setTotalPages(d?.totalPages || 0);
    } catch { /* keep what is shown */ }
  };

  const more = async () => {
    try {
      const d = await getProductReviews(productId, { page: page + 1, tag: tag || undefined });
      setReviews((r) => [...r, ...(d?.reviews || [])]);
      setPage(page + 1);
    } catch { /* keep what is shown */ }
  };

  if (loading) return <div className="h-24 shimmer rounded-2xl" />;
  if (!data?.ratingsPublic) return null;
  if (!data.count) {
    return <p className="text-center text-ink-muted py-6">No reviews yet for this product.</p>;
  }
  const dist = data.distribution || {};
  const tags = data.tags || [];
  const activeTag = tags.find((t) => t.id === tag);
  return (
    <div>
      <div className="flex items-center gap-6 p-5 bg-surface rounded-2xl border border-border mb-6">
        <div className="text-center shrink-0">
          <div className="text-5xl font-black text-ink mb-1">{Number(data.average).toFixed(1)}</div>
          <StarRating rating={Number(data.average)} size="md" />
          <p className="text-xs text-ink-muted mt-1.5">{data.count.toLocaleString('en-IN')} rating{data.count === 1 ? '' : 's'}</p>
        </div>
        <div className="flex-1 space-y-2">
          {['5', '4', '3', '2', '1'].map((r) => {
            const n = Number(dist[r] || 0);
            const pct = Math.round((n / data.count) * 100);
            return (
              <div key={r} className="flex items-center gap-2" data-testid={`dist-${r}`}>
                <span className="text-xs text-ink-soft w-3">{r}</span>
                <span className="text-amber-400 text-xs">★</span>
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} /></div>
                <span className="text-xs text-ink-soft w-8 text-right">{n}</span>
              </div>
            );
          })}
        </div>
      </div>

      {tags.length > 0 && <TagSummary tags={tags} active={tag} onPick={pickTag} clickable={data.textPublic} />}

      {data.textPublic && activeTag && (
        <p className="text-sm text-ink-soft mb-4">
          Reviews that mention <span className="font-semibold text-ink">“{activeTag.label}”</span> ·{' '}
          <button type="button" onClick={() => pickTag(null)} className="text-primary font-semibold hover:underline">Show all</button>
        </p>
      )}

      {data.textPublic && reviews.length > 0 && (
        <ul className="space-y-6">
          {reviews.map((rv) => (
            <li key={rv.id} className="border-b border-border pb-6">
              <div className="flex items-center gap-2"><StarRating rating={rv.rating} size="sm" />
                {rv.title && <span className="font-semibold text-ink">{rv.title}</span>}</div>
              {rv.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {rv.tags.map((t) => <span key={t} className="text-2xs bg-primary-50 text-primary px-2 py-0.5 rounded-full">{t}</span>)}
                </div>
              )}
              {rv.body && <p className="mt-2 text-ink">{rv.body}</p>}
              <p className="text-xs text-ink-soft mt-2">
                {rv.authorName}{rv.authorLocation ? `, ${rv.authorLocation}` : ''} · {sourceText(rv)}
                {rv.reviewedOn ? ` · ${new Date(rv.reviewedOn).toLocaleDateString('en-IN')}` : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
      {data.textPublic && page + 1 < totalPages && (
        <button type="button" onClick={more} className="mt-6 text-sm font-semibold text-primary hover:underline">
          Show more reviews
        </button>
      )}
    </div>
  );
};

export default ProductReviewsPanel;
